import React from "react"
import {useState, useRef} from "react"
import {PROD_URL} from "@/constants"
import {AcapulcoCondoDoc, regionTextByCode} from "@casas-diamante/types"
import {useLocation} from "react-router-dom"
import {getAllInmuebles} from "@/api/properties"
import {getAllCondos} from "@/api/condo"
import {getAssociates} from "@/api/asociados"
import {CSVLink} from "react-csv"
import dayjs from "dayjs"
import {getSeaviewLabel} from "@/types/Seaview"
import {displayZoneLabel} from "@/utils"

interface Asociates {
   [key: string]: string
}

export function CSVPopUpWindow({
   setVisibility,
}: {
   setVisibility: React.Dispatch<React.SetStateAction<boolean>>
}) {
   const headersInm = [
      {label: "Ver Inmueble", key: "verInmueble"},
      {label: "Nombre", key: "name"},
      {label: "Región", key: "region"},
      {label: "Zona", key: "zone"},
      {label: "Dirección", key: "address"},
      {label: "Descripción", key: "desc"},
      {label: "Desc Breve", key: "desc2"},
      {label: "Notas públicas", key: "notes"},
      {label: "Notas privadas", key: "privateNotes"},
      {label: "URL CAD", key: "urlCAD"},
      {label: "Condominio", key: "condo"},
      {label: "Tipo de propiedad", key: "type"},
      {label: "Capacidad", key: "capacity"},
      {label: "Superficie mts2", key: "mts"},
      {label: "Construcción mts2", key: "mtsConstruction"},
      {label: "Pisos", key: "floor"},
      {label: "Niveles", key: "levels"},
      {label: "Habitaciones", key: "rooms"},
      {label: "Baños", key: "bathrooms"},
      {label: "Cajones de estacionamiento", key: "parkingLots"},
      {label: "Precio Mensual Anual", key: "costos.mensual_anual"},
      {label: "Precio Mensual Temporada", key: "costos.mensual_regular"},
      {label: "Precio Noche Alta", key: "costos.noche_alta"},
      {label: "Precio Noche Regular", key: "costos.noche_regular"},
      {label: "Precio Noche Media", key: "costos.noche_media"},
      {label: "Precio por metro", key: "pricePerMeter"},
      {label: "Mantenimiento", key: "maintenance"},
      {label: "Precio Venta", key: "costoVenta"},
      {label: "Vista al mar", key: "seaview"},
      {label: "Amueblado", key: "furnished"},
      {label: "Tipo de amueblado", key: "furnishType"},
      {label: "Ambientes", key: "environments"},
      {label: "Salida a la playa", key: "outing_to_the_beach"},
      {label: "Antigüedad de la casa", key: "house_antiquity"},
      {label: "Disponible", key: "available"},
      {label: "Estatus", key: "status"},
      {label: "Suspendido", key: "suspended"},
      {label: "Fecha de creación", key: "createdAt"},
      {label: "Asociados responables", key: "partners"},
      {label: "URL Maps Embed", key: "mapsUrl"},
      {label: "URL Maps Compartir", key: "mapsShareUrl"},
      {label: "Comisión", key: "comission"},
   ]

   const headersCondo = [
      {
         label: "Ver en página",
         key: "ver",
      },
      {
         label: "Nombre",
         key: "name",
      },
      {
         label: "Región",
         key: "region",
      },
      {
         label: "Descripción",
         key: "desc",
      },

      {
         label: "ID del video",
         key: "videoId",
      },
      {
         label: "Dirección",
         key: "address",
      },
      {
         label: "Zona",
         key: "zone",
      },
      {
         label: "Clasificación",
         key: "class",
      },
      {
         label: "Acceso a la playa",
         key: "access_to_beach",
      },
      {
         label: "Club de playa",
         key: "beach_club",
      },
      {
         label: "Pet friendly",
         key: "pet_friendly",
      },
      {
         label: "Rentas vacacionales permitidas",
         key: "vacation_rentals_allowed",
      },
      {
         label: "Antigüedad",
         key: "antiquity",
      },
      {
         label: "Amenidades",
         key: "amenities",
      },
      {
         label: "Tamaño del condominio",
         key: "condo_size",
      },
      {label: "URL Maps Embed", key: "googleMaps"},
      {label: "URL Maps Compartir", key: "mapsShareUrl"},
   ]

   const [, setEntity] = useState<AcapulcoCondoDoc[] | any[]>([])
   const [asociates, setAsociates] = useState<Asociates>({})
   const [loading, setLoading] = useState<boolean>(false)
   const [csvData, setCsvData] = useState<any[]>([])
   const csvLinkRef = useRef<any>(null)
   const location = useLocation()
   const pathRoute = location.pathname.includes("inmuebles") ? true : false

   async function GetEntityData(path: boolean) {
      setLoading(true)
      let dataEntity

      if (path) {
         dataEntity = await getAllInmuebles()
         setEntity(dataEntity.data)
         const asociates = await getAssociates()
         const asociatesMap = asociates.reduce((acc: any, curr: any) => {
            acc[curr.id] = curr.name
            return acc
         }, {})
         setAsociates(asociatesMap)
         const parsed = ParseDataForCSV(dataEntity.data, path, asociatesMap)
         setCsvData(parsed)
      } else {
         dataEntity = await getAllCondos()
         setEntity(dataEntity)
         const parsed = ParseDataForCSV(dataEntity, path, {})
         setCsvData(parsed)
      }

      setLoading(false)
      // Trigger CSV download after data is ready
      setTimeout(() => {
         csvLinkRef.current && csvLinkRef.current.link.click()
      }, 0)
   }

   function ParseDataForCSV(
      data: AcapulcoCondoDoc[] | any[],
      path: boolean,
      asociatesMap: Asociates = asociates,
   ) {
      if (path) {
         return data.map((inm: any) => ({
            ...inm,
            seaview: getSeaviewLabel(inm.seaview),
            levels: inm.levels && `${inm.levels} ${inm.levels === 1 ? "nivel" : "niveles"}`,
            rooms: `${inm.rooms} ${inm.rooms === 1 ? "habitación" : "habitaciones"}`,
            bathrooms: `${inm.bathrooms} ${inm.bathrooms === 1 ? "baño" : "baños"}`,
            parkingLots: `${inm.parkingLots} ${inm.parkingLots === 1 ? "cajón" : "cajones"}`,
            floor: inm.floor && `Piso ${inm.floor}`,
            costos: {
               mensual_anual: inm.costos?.mensual_anual?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "MXN",
               }),
               mensual_regular: inm.costos?.mensual_regular?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "MXN",
               }),
               noche_alta: inm.costos?.noche_alta?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "MXN",
               }),
               noche_regular: inm.costos?.noche_regular?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "MXN",
               }),
               noche_media: inm.costos?.noche_media?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "MXN",
               }),
            },
            pricePerMeter: inm.pricePerMeter?.toLocaleString("en-US", {
               style: "currency",
               currency: "MXN",
            }),
            maintenance: inm.maintenance?.toLocaleString("en-US", {
               style: "currency",
               currency: "MXN",
            }),
            costoVenta: inm.costoVenta?.toLocaleString("en-US", {
               style: "currency",
               currency: "MXN",
            }),
            environments: inm.environments?.join(", "),
            partners: inm.partners?.map((partner: string) => asociatesMap[partner]).join(", "),
            createdAt: inm.createdAt && dayjs.unix(inm.createdAt._seconds).format("DD-MM-YYYY"),
            verInmueble: `${PROD_URL}/inmuebles/${inm.id}`,
            desc: inm.desc?.replace(/"/g, "'"),
            notes: inm.notes?.replace(/"/g, "'"),
            privateNotes: inm.privateNotes?.replace(/"/g, "'"),
            desc2: inm.desc2?.replace(/"/g, "'"),
            region: regionTextByCode[inm.region as keyof typeof regionTextByCode],
            zone: inm.zone ? displayZoneLabel(inm.zone) : inm.zone,
            comission: inm.forSale && inm.saleComission != null ? `${inm.saleComission}%` : "Sin comisión",
         }))
      } else {
         return data.map((condo: AcapulcoCondoDoc) => ({
            ...condo,
            amenities: condo.amenities?.join(", "),
            class: condo.class ? condo.class : "sin clasificar",
            ver: `${PROD_URL}/condominios/${condo.id}`,
            desc: condo.desc?.replace(/"/g, "'"),
            region: regionTextByCode[condo.region],
            zone: condo.zone ? displayZoneLabel(condo.zone) : condo.zone,
         }))
      }
   }

   return (
      <div
         id="PopUpCSV"
         className="flex flex-col bg-white rounded border border-black p-4 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 max-w-full sm:max-w-lg w-[90%]"
      >
         <h3 className="mt-2 mb-2">Informacion en vista de Excel</h3>
         <p className="mt-2 mb-2">
            ¿Quieres descargar los {pathRoute ? "inmuebles" : "condominios"} en formato CSV?
         </p>
         <div id="button container download" className="flex justify-center">
            <button
               className="rounded bg-red p-2 mb-5 text-white font-bold w-[200px] mt-4 mr-2"
               onClick={() => setVisibility(false)}
            >
               Cerrar
            </button>
            <button
               className="rounded bg-green p-2 mb-5 text-white font-bold w-[200px] mt-4 ml-2"
               onClick={() => GetEntityData(pathRoute)}
               disabled={loading}
            >
               {loading ? "Preparando..." : "Descargar modo excel"}
            </button>
         </div>
         <CSVLink
            headers={pathRoute ? headersInm : headersCondo}
            data={csvData}
            filename={`export-${pathRoute ? "inmuebles" : "condominios"}-${dayjs().format(
               "YYYY-MM-DD",
            )}.csv`}
            className="hidden"
            ref={csvLinkRef}
            target="_blank"
         />
      </div>
   )
}
